import { useTranslation } from 'react-i18next';
import { useClock } from '@/shared/hooks/useClock';
import { getTimeParts, formatDateNaturalLong } from '@/shared/utils/datetime';
import type { ThemeTokens } from '@/shared/ui/theme/tokens';

interface ClockDisplayProps {
  T: ThemeTokens;
}

export function ClockDisplay({ T }: ClockDisplayProps) {
  const { i18n } = useTranslation();
  const now = useClock();
  const { h, m, s, ampm } = getTimeParts(now);
  // Locale resolution: only es / en are supported. Anything else falls back to es
  // so we keep parity with the kiosk's default (Clock_Web.html is es-first).
  const locale: 'es' | 'en' = i18n.language.startsWith('en') ? 'en' : 'es';

  return (
    <div style={{ textAlign: 'center', marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 6, lineHeight: 1 }}>
        <span
          style={{
            fontFamily: 'Lato,sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(48px,6.75vw,81px)',
            color: T.clockText,
            letterSpacing: '-3px',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {h}:{m}:{s}
        </span>
        {/* AM/PM — letterSpacing normal para matchear el mockup (medido
            2026-07-16: `letter-spacing: normal`). Sin este override hereda
            el -0.02em global de `index.css` y las 2 letras quedan más juntas
            que en el mockup. */}
        <span
          style={{
            fontFamily: 'Lato,sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(17px,2.25vw,24px)',
            color: T.textBody,
            paddingBottom: 10,
            whiteSpace: 'nowrap',
            letterSpacing: 'normal',
          }}
        >
          {ampm}
        </span>
      </div>
      {/* Fecha — letterSpacing normal (mismo motivo que AM/PM). */}
      <div style={{ fontSize: 12, fontWeight: 500, color: T.textBody, marginTop: 8, letterSpacing: 'normal' }}>
        {formatDateNaturalLong(now, locale)}
      </div>
    </div>
  );
}
